import {useEffect, useMemo} from 'react';
import {useInfiniteQuery} from '@tanstack/react-query';
import {fetchUnifiedMessages, unifiedMessagesQueryKey} from '@/api/messages';
import {UnifiedFeedItem, UnifiedFeedPage} from '@/api/messages/types';
import {MESSAGES_POLL_INTERVAL_MS} from '@/api/messages/constants';

export default function useUnifiedMessagesQuery(
  conversationId: string | null,
  campaignId: string | null,
  {enabled = true}: {enabled?: boolean} = {}
) {
  const isEnabled = enabled && Boolean(conversationId && campaignId);
  const query = useInfiniteQuery<UnifiedFeedPage>({
    queryKey: unifiedMessagesQueryKey(conversationId ?? '', campaignId ?? ''),
    queryFn: ({pageParam}) =>
      fetchUnifiedMessages(
        conversationId as string,
        campaignId as string,
        pageParam as string | undefined
      ),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: isEnabled,
    refetchOnWindowFocus: true,
  });
  const {refetch} = query;

  useEffect(() => {
    if (!isEnabled) return undefined;
    const interval = window.setInterval(() => {
      refetch({refetchPage: (_page, index) => index === 0});
    }, MESSAGES_POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [isEnabled, refetch]);

  const items = useMemo<UnifiedFeedItem[]>(
    () => query.data?.pages.flatMap((page) => page.items) ?? [],
    [query.data]
  );

  return {
    ...query,
    items,
    firstUnreadMessageId: query.data?.pages[0]?.firstUnreadMessageId ?? null,
    isLoading: isEnabled && query.isLoading,
  };
}
